import {
  EducationDetails,
  EmployeeRelations,
  EmploymentHistory,
  FamilyDetails,
  PrevApplications,
  References,
} from "../../../../types/Form-types";
import {
  EvaluationFormData,
  Interviewer,
} from "../../interview-assessment-form/EvaluationSheet";
import {
  required,
  requiredSelect,
  validateForm,
  ValidationSchema,
  indianPhone,
  validEmail,
  positiveNumber,
  yyyymmdd,
  FormErrors,
} from "../validation-engine/ValidationEngine";

// ─── return types ─────────────────────────────────────────────────────────────
export type FlatErrors = Record<string, string | undefined>;

// ─── Field Labels ─────────────────────────────────────────────────────────────
const fieldLabels: Record<string, string> = {
  source: "Source",
  position: "Position",
  candidateName: "Candidate name",
  gender: "Gender",
  dateOfBirth: "Date of birth",
  department: "Department",
  Address: "Address",
  permanentAddress: "Permanent address",
  mobileNo: "Mobile number",
  maritalStatus: "Marital status",
  emailId: "Email ID",
  langSpeak: "Language (speak)",
  langRead: "Language (read)",
  langWrite: "Language (write)",
  careerGoal: "Career goal",
  strength: "Strength",
  weakness: "Weakness",
  current_ctc: "Current CTC",
  expected_ctc: "Expected CTC",
  notice_period_days: "Notice period",
  convicted_in_court: "Convicted in court",
  final_hr_recommendation: "Final HR recommendation",
  grade_designation_allocated: "Grade/designation allocated",
  ctc_to_be_offered: "CTC to be offered",
  onboarding_date: "Onboarding date",
};

export const evaluationBlockLabels: Record<string, string> = {
  interviewer1: "Interviewer 1",
  interviewer2: "Interviewer 2",
  hrHead: "HR Head",
};

export const arraySectionLabels: Record<keyof ArrayErrors, string> = {
  education_details: "Education details",
  family_details: "Family details",
  employment_history: "Employment history",
  employee_relations: "Employee relations",
  prev_applications: "Previous applications",
  references: "References",
};

export const evaluationFieldLabels: Partial<Record<string, string>> = {
  interviewer_name: "Interviewer name",
  interview_datetime: "Interview date & time",
  selection_criteria: "Selection criteria",
  rating_technical_knowledge: "Technical knowledge rating",
  rating_functional_qualification: "Functional qualification rating",
  rating_communication_skills: "Communication skills rating",
  rating_confidence_level: "Confidence level rating",
  rating_body_language: "Body language rating",
  recommendation: "Recommendation",
  remarks: "Remarks",
};

export type ArrayErrors = {
  education_details?: Partial<Record<keyof EducationDetails, string>>[];
  family_details?: Partial<Record<keyof FamilyDetails, string>>[];
  employment_history?: Partial<Record<keyof EmploymentHistory, string>>[];
  employee_relations?: Partial<Record<keyof EmployeeRelations, string>>[];
  prev_applications?: Partial<Record<keyof PrevApplications, string>>[];
  references?: Partial<Record<keyof References, string>>[];
};

export type InterviewFormErrors = FlatErrors & ArrayErrors;

// ─── array row validator ──────────────────────────────────────────────────────

const validateArrayRows = <T extends object>(
  rows: T[],
  fields: (keyof T)[],
  fieldMessages?: Partial<Record<keyof T, string>>,
  identifierField?: keyof T, // ✅ new param
  fieldValidators?: Partial<Record<keyof T, (v: string) => string | undefined>>,
): Partial<Record<keyof T, string>>[] | undefined => {
  const result = rows.map((row) => {
    const rowErrors: Partial<Record<keyof T, string>> = {};
    const hasAnyValue = fields.some((f) => String(row[f] ?? "").trim() !== "");
    if (!hasAnyValue) return rowErrors;

    // fields.forEach((f) => {
    //   if (String(row[f] ?? "").trim() === "") {
    //     rowErrors[f] = fieldMessages?.[f] ?? "Required!";
    //   }
    // });

    fields.forEach((f) => {
      const val = String(row[f] ?? "").trim();
      if (val === "") {
        rowErrors[f] = fieldMessages?.[f] ?? "Required!";
      } else if (fieldValidators?.[f]) {
        // ✅ run format validator only if value exists
        const validationError = fieldValidators[f]!(val);
        if (validationError) rowErrors[f] = validationError;
      }
    });

    // ✅ attach identifier value when row has errors
    if (identifierField && Object.keys(rowErrors).length > 0) {
      rowErrors[identifierField] = String(row[identifierField] ?? "") as any;
    }

    return rowErrors;
  });

  const hasAnyError = result.some((r) => Object.keys(r).length > 0);
  return hasAnyError ? result : undefined;
};

// check min-age
const minAge =
  (min: number) =>
  (v: string): string => {
    const dob = new Date(v);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const hasBirthdayPassed =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
    const actualAge = hasBirthdayPassed ? age : age - 1;
    return actualAge >= min ? "" : `Must be at least ${min} years old!`;
  };
// ─── flat schema ──────────────────────────────────────────────────────────────
export const interviewSchema: ValidationSchema<any> = {
  source: [requiredSelect()],
  position: [required()],
  candidateName: [required()],
  gender: [requiredSelect()],
  dateOfBirth: [required(), minAge(17)],
  department: [required()],
  Address: [required()],
  permanentAddress: [required()],
  mobileNo: [required(), indianPhone()],
  maritalStatus: [requiredSelect()],
  emailId: [required(), validEmail()],
  langSpeak: [(v) => (!v?.length ? "Required!" : "")], // ✅ CHANGED: required() → array length check
  langRead: [(v) => (!v?.length ? "Required!" : "")],
  langWrite: [(v) => (!v?.length ? "Required!" : "")],
  // langSpeak: [required()],
  // langRead: [required()],
  // langWrite: [required()],
  careerGoal: [required()],
  strength: [required()],
  weakness: [required()],
  current_ctc: [required(), positiveNumber()],
  expected_ctc: [required(), positiveNumber()],
  notice_period_days: [required(), positiveNumber()],
  convicted_in_court: [requiredSelect()],
  final_hr_recommendation: [required()],
  grade_designation_allocated: [required()],
  ctc_to_be_offered: [required(), positiveNumber()],
  onboarding_date: [required(), yyyymmdd()],
};

// ─── full validator ───────────────────────────────────────────────────────────
export const validateInterviewForm = (formData: any): InterviewFormErrors => {
  const flatErrors: FlatErrors = validateForm(formData, interviewSchema);

  const arrayErrors: ArrayErrors = Object.fromEntries(
    Object.entries({
      education_details: validateArrayRows<EducationDetails>(
        formData.education_rows ?? formData.education_details ?? [],
        [
          "qualification",
          "duration_from",
          "duration_to",
          "university_school",
          "major_subjects",
          "percentage_cgpa",
        ],
        undefined,
        undefined,
        {
          percentage_cgpa: (val) => {
            // console.log("percentage_cgpa val →", val);
            const num = parseFloat(val);
            if (isNaN(num)) return "Must be a valid number!";
            if (num > 100) return "Cannot be greater than 100!";
            if (num < 0) return "Cannot be negative!";
            return undefined;
          },
        },
        // {
        //   duration_from: "Duration must be in MM/YY format!",
        //   duration_to: "Duration must be in MM/YY format!",
        // },
      ),

      // family_details: validateArrayRows<FamilyDetails>(
      //   formData.family_details ?? [],
      //   ["relationship", "name", "occupation"],
      // ),
      family_details: validateArrayRows<FamilyDetails>(
        formData.family_details ?? [],
        ["name", "occupation"],
        undefined,
        "relationship", // ✅ only for family
      ),

      employment_history: validateArrayRows<EmploymentHistory>(
        formData.employment_history ?? [],
        [
          "organization",
          "duration_from",
          "duration_to",
          "last_designation",
          "last_drawn_ctc",
          "reason_for_change",
        ],
        // {
        //   duration_from: "Duration must be in MM/YY format!",
        //   duration_to: "Duration must be in MM/YY format!",
        // },
      ),

      employee_relations: validateArrayRows<EmployeeRelations>(
        formData.employee_relations ?? [],
        ["name", "designation", "relationship"],
      ),

      prev_applications: validateArrayRows<PrevApplications>(
        formData.prev_applications ?? [],
        ["year_month_applied", "position", "location"],
      ),

      references: validateArrayRows<References>(
        formData.references ?? [],
        ["name_of_referee", "designation", "organization", "contact_number"],
        { contact_number: "Required" },
        "s_no", // ✅ identifier
        {
          contact_number: (v) => {
            if (v.length > 10) return "Contact number cannot exceed 10 digits!";
            if (!/^[6-9]\d{9}$/.test(v))
              return "Must be a valid 10-digit Indian number!";
            return undefined;
          },
        },
      ),
      // references: validateArrayRows<References>(
      //   formData.references ?? [],
      //   ["name_of_referee", "designation", "organization", "contact_number"],
      //   { contact_number: "Must be a valid 10-digit Indian number!" },
      // ),
    }).filter(([_, v]) => v !== undefined), // ✅ removes keys with undefined
  ) as ArrayErrors;

  return { ...flatErrors, ...arrayErrors } as InterviewFormErrors;
};

export const getFirstErrorMessage = (
  interviewErrors: InterviewFormErrors,
  evalErrors: Record<string, any>,
): string | null => {
  // ─── 1. Flat fields in priority order ────────────────────────────────────
  const flatFieldOrder = [
    "source",
    "position",
    "candidateName",
    "gender",
    "dateOfBirth",
    "department",
    "Address",
    "permanentAddress",
    "mobileNo",
    "maritalStatus",
    "emailId",
    "langSpeak",
    "langRead",
    "langWrite",
    "careerGoal",
    "strength",
    "weakness",
    "current_ctc",
    "expected_ctc",
    "notice_period_days",
    "convicted_in_court",
    "final_hr_recommendation",
    "grade_designation_allocated",
    "ctc_to_be_offered",
    "onboarding_date",
  ];

  for (const key of flatFieldOrder) {
    const error = interviewErrors[key];
    if (typeof error === "string" && error) {
      const label = fieldLabels[key] ?? key;
      const isGenericRequired =
        error.toLowerCase().includes("required") ||
        error.toLowerCase().includes("this field");
      return isGenericRequired ? `${label} is required` : `${label}: ${error}`;
    }
  }

  // ─── 2. Array sections in priority order ──────────────────────────────────
  const arraySectionOrder: (keyof ArrayErrors)[] = [
    "education_details",
    "family_details",
    "employment_history",
    "employee_relations",
    "prev_applications",
    "references",
  ];

  for (const sectionKey of arraySectionOrder) {
    const sectionErrors = interviewErrors[sectionKey];
    if (Array.isArray(sectionErrors)) {
      const hasError = sectionErrors.some((row) => Object.keys(row).length > 0);
      if (hasError) {
        return `Please fill all required fields in "${arraySectionLabels[sectionKey]}"`;
      }
    }
  }

  // ─── 3. Evaluation block errors ───────────────────────────────────────────
  for (const block of ["interviewer1", "interviewer2", "hrHead"]) {
    const blockErrors = evalErrors[block];
    if (blockErrors && typeof blockErrors === "object") {
      for (const field of Object.keys(blockErrors)) {
        if (blockErrors[field]) {
          const blockLabel = evaluationBlockLabels[block];
          const fieldLabel = evaluationFieldLabels[field] ?? field;
          return `${blockLabel} — ${fieldLabel} is required`;
        }
      }
    }
  }

  return null;
};

// ------- evaluation validations --------

export type EvaluationFormErrors = FormErrors<EvaluationFormData>;

const interviewerSchema: ValidationSchema<Interviewer> = {
  interviewer_name: [required()],
  interview_datetime: [required()],
  selection_criteria: [requiredSelect()],
  rating_technical_knowledge: [requiredSelect()],
  rating_functional_qualification: [requiredSelect()],
  rating_communication_skills: [requiredSelect()],
  rating_confidence_level: [requiredSelect()],
  rating_body_language: [requiredSelect()],
  recommendation: [requiredSelect()],
  remarks: [required()],
};

export const evaluationSchema = {
  interviewer1: interviewerSchema,
  interviewer2: interviewerSchema,
  hrHead: interviewerSchema,
  final_hr_recommendation: [required()],
  grade_designation_allocated: [required()],
  ctc_to_be_offered: [required()],
  onboarding_date: [required()],
};

export const validateEvaluationForm = (
  formData: EvaluationFormData,
): EvaluationFormErrors => {
  // validate flat fields only
  // const flatSchema = {
  //   final_hr_recommendation: [required()],
  //   grade_designation_allocated: [required()],
  //   ctc_to_be_offered: [required()],
  //   onboarding_date: [required()],
  // };

  // const flatErrors = validateForm(
  //   formData as unknown as Record<string, unknown>,
  //   flatSchema,
  // );

  // validate each interviewer block separately
  const interviewer1Errors = validateForm(
    formData.interviewer1 as unknown as Record<string, unknown>,
    interviewerSchema,
  );
  const interviewer2Errors = validateForm(
    formData.interviewer2 as unknown as Record<string, unknown>,
    interviewerSchema,
  );
  const hrHeadErrors = validateForm(
    formData.hrHead as unknown as Record<string, unknown>,
    interviewerSchema,
  );

  return {
    // ...flatErrors,
    ...(Object.keys(interviewer1Errors).length && {
      interviewer1: interviewer1Errors,
    }),
    ...(Object.keys(interviewer2Errors).length && {
      interviewer2: interviewer2Errors,
    }),
    ...(Object.keys(hrHeadErrors).length && { hrHead: hrHeadErrors }),
  } as EvaluationFormErrors;
};
